import isNumber from 'lodash/isNumber';
import {createContext, useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

import {distributorT} from '@/lib/types/distributor.types';
import {marketT} from '@/lib/types/market.types';
import {retailerT} from '@/lib/types/retailer.types';

import {defaultCreateData} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks';

type MarketDetailsProviderT = {
  // Properties
  market: marketT | undefined;
  marketId: number;
  isLoading: boolean;
  error: Error | null;
  // Functions
  searchDistributorsToAdd: (name: string) => Promise<distributorT[]>;
  addDistributorToMarket: (distributor_id: number) => Promise<void>;
  removeDistributorFromMarket: (distributor_id: number) => Promise<void>;
  searchRetailersToAdd: (name: string) => Promise<retailerT[]>;
  addRetailerToMarket: (retailer_id: number) => Promise<void>;
  removeRetailerFromMarket: (retailer_id: number) => Promise<void>;
};

export const MarketDetailsContext = createContext({} as MarketDetailsProviderT);

/** The provider for the market details */
export const MarketDetailsProvider = ({children}: {children: React.ReactNode}) => {
  const navigate = useNavigate();
  const {id: marketIdFromParams} = useParams();
  const marketId = Number(marketIdFromParams);

  // State
  const [isLoading, setIsLoading] = useState(true);

  // TanStack query
  const {data: market, error} = useItemQuery({
    table: 'Market',
    id: marketId,
    enabled: !!marketId,
  });

  // Initialize the market details
  useEffect(() => {
    // If we have already fetched the market's details or do not yet have a
    // market id from the param hook, return
    if (!marketId) return;

    // Some error handling
    if (!isNumber(marketId) || error) {
      navigate('/admin/markets');
      if (error) console.error('Error getting market:', error);
      return;
    }

    // If we have a market, stop loading
    if (market) setIsLoading(false);
  }, [error, market, marketId, navigate]);

  /**
   * Adds a distributor to the market
   */
  const addDistributorToMarket = async (distributorId: number) => {
    try {
      const createData = await defaultCreateData();
      const {error} = await SupabaseInstance.supabase.from('Market_Distributor').upsert(
        {
          ...createData,
          fk_distributor_id: distributorId,
          fk_market_id: marketId,
          date_removed: null,
          removed_by: null,
        },
        {
          onConflict: 'fk_distributor_id,fk_market_id',
        }
      );

      if (error) throw new Error(error?.message || 'Error adding distributor to market');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a distributor from the market
   */
  const removeDistributorFromMarket = async (distributorId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Market_Distributor')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_distributor_id', distributorId)
        .eq('fk_market_id', marketId);

      if (error) throw new Error(error?.message || 'Error removing distributor from market');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Function used to search for distributors to add to the market
   */
  const searchDistributorsToAdd = async (name: string) => {
    try {
      // Step 2: Build the query
      const query = SupabaseInstance.supabase.from('Distributor').select('*').ilike('distributor_name', `%${name}%`);

      // Execute the query
      const {data, error} = await query;

      if (error) {
        console.error('Error in searchDistributorsToAdd:', error);
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error in searchDistributorsToAdd:', error);
      throw error;
    }
  };

  /*
   * Function used to search for retailers to add to the market
   */
  const searchRetailersToAdd = async (name: string) => {
    try {
      // Search for retailers by name, excluding those already associated with the market
      const {data, error} = await SupabaseInstance.supabase
        .from('Retailer')
        .select()
        .ilike('retailer_name', `%${name}%`);

      if (error) {
        console.error('Error in searchRetailersToAdd:', error);
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error in searchRetailersToAdd:', error);
      throw error;
    }
  };

  /*
   * Function used to remove a retailer from the market
   */
  const removeRetailerFromMarket = async (retailerId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Market_Retailer')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_retailer_id', retailerId)
        .eq('fk_market_id', marketId);

      if (error) throw new Error(error?.message || 'Error removing retailer from market');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Function used to add a retailer to the market
   */
  const addRetailerToMarket = async (retailerId: number) => {
    try {
      const createData = await defaultCreateData();

      const data = {
        ...createData,
        fk_retailer_id: retailerId,
        fk_market_id: marketId,
        date_removed: null,
        removed_by: null,
      };

      const {error} = await SupabaseInstance.supabase
        .from('Market_Retailer')
        .upsert(data, {
          onConflict: 'fk_retailer_id,fk_market_id',
        })
        .select()
        .single();

      if (error) {
        console.error('error in add retailer to market', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in add retailer to market', error);
      throw new Error((error as Error).message);
    }
  };

  return (
    <MarketDetailsContext.Provider
      value={{
        market,
        isLoading,
        marketId,
        error,
        searchDistributorsToAdd,
        addDistributorToMarket,
        removeDistributorFromMarket,
        searchRetailersToAdd,
        addRetailerToMarket,
        removeRetailerFromMarket,
      }}
    >
      {children}
    </MarketDetailsContext.Provider>
  );
};

/** Hook to use the market details */
export const useMarket = () => {
  const context = useContext(MarketDetailsContext);

  // Ensure the context is available
  if (!context) {
    throw new Error('useMarket must be used within a MarketDetailsProvider');
  }

  return context;
};
